import DirectionsOutlinedIcon from '@mui/icons-material/DirectionsOutlined';
import RoomIcon from '@mui/icons-material/Room';
import { Typography, useTheme } from '@mui/material';
import hexToRgba from 'hex-to-rgba';
import i18next from 'i18next';
import { memo, ReactNode } from 'react';

import { PointOfSale } from '../../../../interface';
import { numberFormatter } from '../../../../utils/formatter';
import { formatCenterAddress } from '../../../../utils/multi-store/text';

interface MultiStoreCenterContentProps {
  pointOfSale: PointOfSale;
  // centro di partenza: badge «Il tuo centro» e nessuna riga distanza
  isOrigin: boolean;
  distance?: number;
  isAddressVisible?: boolean;
  // a destra del contenuto, per esempio l'icona di stato del pannello
  adornment?: ReactNode;
  // righe aggiuntive sotto la distanza (stato all'orario scelto, orari più vicini)
  children?: ReactNode;
}

// «3,2 km»: una cifra decimale col formatter dello step "dove"
const formatDistance = (distance: number): string =>
  i18next.t('pages.when.multi_store.km', {
    km: numberFormatter(distance, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
  });

// Contenuto della scheda di un centro ottico, come nello step "dove": nome in grassetto,
// riga indirizzo e riga distanza con le icone primary
export const MultiStoreCenterContent = memo(function MultiStoreCenterContent({
  pointOfSale,
  isOrigin,
  distance,
  isAddressVisible = true,
  adornment,
  children,
}: MultiStoreCenterContentProps): JSX.Element {
  const { palette } = useTheme();
  const address = formatCenterAddress(pointOfSale.address, pointOfSale.city);

  return (
    <div className="multi-store-card">
      <div className="multi-store-card-body">
        <div className="multi-store-card-title">
          <Typography variant="contentDescription1">
            <b>{pointOfSale.name}</b>
          </Typography>
          {isOrigin && (
            <span
              className="multi-store-badge"
              style={{
                backgroundColor: hexToRgba(
                  palette.primary.main,
                  palette.mode === 'dark' ? 0.3 : 0.12
                ),
                color: palette.text.primary,
              }}
            >
              <Typography variant="contentDescription2">
                {i18next.t('pages.when.multi_store.your_center')}
              </Typography>
            </span>
          )}
        </div>
        {isAddressVisible && address !== '' && (
          <div className="multi-store-card-line">
            <DirectionsOutlinedIcon color="primary" />
            <Typography variant="contentDescription1">{address}</Typography>
          </div>
        )}
        {!isOrigin && distance !== undefined && (
          <div className="multi-store-card-line">
            <RoomIcon color="primary" />
            <Typography variant="contentDescription2">
              {formatDistance(distance)}
            </Typography>
          </div>
        )}
        {children}
      </div>
      {adornment !== undefined && (
        <div className="multi-store-card-adornment">{adornment}</div>
      )}
    </div>
  );
});
